/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { uniq } from 'fp-ts/lib/Array';
import { eqString } from 'fp-ts/lib/Eq';
import { fromPredicate, none, some } from 'fp-ts/lib/Option';
import { Coordinate } from 'ol/coordinate';
import { ChangeEvent, KeyboardEvent } from 'react';
import { getAlias, getLang } from '../app';
import {
    DIV,
    INPUT,
    NodeOrOptional,
    SPAN,
    STRONG,
} from '../components/elements';
import { renderSelect } from '../components/input';
import { isENTER } from '../components/keycodes';
import {
    clearServiceResponse,
    selectService,
    updateTerm,
} from '../geocoder/events';
import { queryService } from '../geocoder/index';
import {
    areLayerResults,
    LayerParameter,
    LayerResult,
    serviceMessageToString,
    ServiceResult,
    ServiceDescription,
    TermResult,
} from '../geocoder/io';
import {
    getAllServices,
    getSelectedService,
    getServiceName,
    getServicePlaceholder,
    getServiceResponse,
    getTerm,
} from '../geocoder/queries';
import { fromRecord, tr, Translated } from '../locale';
import { IMapViewData, Interaction, viewEventsFactory } from '../map';
import { Getter, Setter } from '../shape/types';
import { FeatureViewConfig, FeatureViewOptions, ILayerInfo } from '../source';
import { makeIcon } from './button';
import { Tooltip } from './tooltip';
import { tryNameToString } from './button/names';

const coord = (result: TermResult): [number, number] => [
    result.coord.x,
    result.coord.y,
];

export type TermAction = (result: TermResult) => NodeOrOptional;

export type LayerAction = (
    layer: ILayerInfo,
    field: string,
    searchQuery: string,
    featureIds: number[] | string[],
) => NodeOrOptional;

const putMark = (
    coordinates: Coordinate,
    setInteraction: Setter<Interaction>,
) =>
    setInteraction(() => ({
        label: 'mark',
        state: {
            started: false,
            endTime: Date.now() + 3000,
            coordinates,
        },
    }));

const updateServiceTerm = (e: ChangeEvent<HTMLInputElement>) => {
    updateTerm(e.target.value);
};

const isConfigView = (opts: FeatureViewOptions): opts is FeatureViewConfig =>
    opts.type === 'config';

const getSearchableFields = (layer: ILayerInfo) =>
    fromPredicate(isConfigView)(layer.featureViewOptions).map(opts =>
        uniq(eqString)(opts.rows.map(row => row.propName)).filter(
            field => !field.startsWith('__WIDGET'),
        ),
    );

const getLayerQueryParam = (
    getMapLayerInfos: Getter<ILayerInfo[]>,
): LayerParameter[] =>
    getMapLayerInfos().map(info =>
        getSearchableFields(info).fold<LayerParameter>(
            { layerId: info.id },
            fields => ({ layerId: info.id, fields }),
        ),
    );

const searchService = (
    serviceCode: string,
    getMapLayerInfos: Getter<ILayerInfo[]>,
) => {
    if (serviceCode === 'layers') {
        queryService({
            service: serviceCode,
            input: getTerm(),
            lang: getLang(),
            layers: getLayerQueryParam(getMapLayerInfos),
        });
    } else {
        queryService({
            service: serviceCode,
            input: getTerm(),
            lang: getLang(),
        });
    }
};

const zoomTo = (
    coords: Coordinate,
    setInteraction: Setter<Interaction>,
    setView: Setter<IMapViewData>,
) => {
    const viewEvents = viewEventsFactory(setView);
    clearServiceResponse();
    viewEvents.updateMapView({
        dirty: 'geo',
        center: coords,
        zoom: 12,
    });
    putMark(coords, setInteraction);
};

const tooltip = (actionName: string, result: TermResult): Tooltip => ({
    text: `${actionName}\n${serviceMessageToString(result.message)}` as Translated,
    position: 'bottom-right',
});

const centerTooltip = (result: TermResult) =>
    tooltip(tr.core('zoomToAddress'), result);

const centerButton = (result: TermResult) =>
    makeIcon('select', 3, 'map-marker-alt', centerTooltip(result));

const selectButton = (actionName: string, result: TermResult) =>
    makeIcon('select', 3, 'play', tooltip(actionName, result));

export const makeActionSelect =
    (actionName: string, action: (c: Coordinate) => void): TermAction =>
    result => {
        return selectButton(actionName, result)(() => action(coord(result)));
    };

const tableButton = makeIcon('table', 3, 'table', {
    text: () => tr.core('tooltip:dataAccess'),
    position: 'right',
});

export const layerActionTable =
    (
        onSelect: (layer: ILayerInfo, field: string, q: string) => void,
    ): LayerAction =>
    (layer, field, q) =>
        tableButton(() => onSelect(layer, field, q));

const selectOnMapButton = makeIcon('select', 3, 'mouse-pointer', {
    text: () => tr.core('tooltip:selectOnMap'),
    position: 'right',
});

export const layerActionSelectOnMap =
    (
        onSelect: (layerId: string, featureIds: number[] | string[]) => void,
    ): LayerAction =>
    (layer, _field, _q, featureIds) =>
        selectOnMapButton(() => onSelect(layer.id, featureIds));

export interface LayerResultCount {
    layerId: string;
    field: string;
    featureIds: number[] | string[];
}

export const countLayerResults = (results: LayerResult[]): LayerResultCount[] =>
    Array.from(
        results
            .reduce((acc, { layerId, field, featureId }) => {
                const key = `${layerId}\0${field}`;
                const prev = acc.get(key) ?? {
                    layerId,
                    field,
                    featureIds: [],
                };
                return acc.set(key, {
                    ...prev,
                    // asserting because we all results will have the same type
                    // for featureId
                    featureIds: [...prev.featureIds, featureId] as
                        | number[]
                        | string[],
                });
            }, new Map<string, LayerResultCount>())
            .values(),
    );

const renderLayerResults = (
    results: LayerResult[],
    getMapLayerInfos: Getter<ILayerInfo[]>,
    layerActions: LayerAction[],
) => {
    const layerIndex = new Map(getMapLayerInfos().map(info => [info.id, info]));
    const layerName = (layerId: string) => {
        const legend = layerIndex.get(layerId)?.legend;
        return legend ? fromRecord(legend) : layerId;
    };
    const searchQuery = getTerm();
    return countLayerResults(results).map(
        ({ layerId, field, featureIds }, key) =>
            DIV(
                { className: 'result-select', key },
                DIV(
                    { id: `geocoder-result-${key}`, className: 'result-label' },
                    SPAN('layer-name', layerName(layerId)),
                    STRONG(
                        'field',
                        SPAN('fa picto', tryNameToString('arrow-right')),
                        getAlias(field),
                    ),
                ),
                DIV(
                    'result-actions',
                    SPAN('count', `${featureIds.length}`),
                    ...layerActions.map(a =>
                        a(
                            layerIndex.get(layerId)!,
                            field,
                            searchQuery,
                            featureIds,
                        ),
                    ),
                ),
            ),
    );
};

const renderTermResult = (
    key: number,
    result: ServiceResult,
    setView: Setter<IMapViewData>,
    setInteraction: Setter<Interaction>,
    termActions: TermAction[] = [],
) =>
    DIV(
        { className: 'result-select', key },
        SPAN(
            {
                id: `geocoder-result-${key}`,
                className: 'result-label',
                onClick: () => zoomTo(coord(result), setInteraction, setView),
            },
            serviceMessageToString(result.message),
        ),
        DIV(
            'result-actions',
            centerButton(result)(() =>
                zoomTo(coord(result), setInteraction, setView),
            ),
            ...termActions.map(a => a(result)),
        ),
    );

const renderResults = (
    results: ServiceResult[],
    getMapLayerInfos: Getter<ILayerInfo[]>,
    termConfig: {
        setView: Setter<IMapViewData>;
        setInteraction: Setter<Interaction>;
        termActions?: TermAction[];
    },
    layerActions: LayerAction[],
) =>
    areLayerResults(results)
        ? renderLayerResults(results, getMapLayerInfos, layerActions)
        : results.map((result, key) =>
              renderTermResult(
                  key,
                  result,
                  termConfig.setView,
                  termConfig.setInteraction,
                  termConfig.termActions,
              ),
          );

const btnSearch = makeIcon('search', 1, 'search', {
    position: 'right',
    text: () => tr.core('search'),
});

export const renderService = (
    placeholder: string,
    serviceCode: string,
    getMapLayerInfos: Getter<ILayerInfo[]>,
    termConfig: {
        setView: Setter<IMapViewData>;
        setInteraction: Setter<Interaction>;
        termActions?: TermAction[];
    },
    layerActions: LayerAction[],
) =>
    DIV(
        'search-widget',
        DIV(
            'search',
            INPUT({
                type: 'text',
                name: 'adress',
                placeholder: `${placeholder}`,
                onChange: updateServiceTerm,
                onKeyPress: (e: KeyboardEvent<HTMLInputElement>) => {
                    if (isENTER(e)) {
                        searchService(serviceCode, getMapLayerInfos);
                    }
                },
            }),
            btnSearch(() => searchService(serviceCode, getMapLayerInfos)),
        ),
        getServiceResponse().map(r =>
            r.results.length > 0
                ? DIV(
                      'search-results',
                      renderResults(
                          r.results,
                          getMapLayerInfos,
                          termConfig,
                          layerActions,
                      ),
                  )
                : DIV('search-results', DIV('', tr.core('geocodeNoResults'))),
        ),
    );

const renderServiceList = renderSelect(
    `geocoder-service`,
    getServiceName,
    selectService,
    eqString,
);

export const renderServiceOrList = (services: ServiceDescription[]) => {
    switch (services.length) {
        case 0:
            return none;
        case 1:
            return some(DIV('mono', getServiceName(services[0].code)));
        default:
            return some(
                renderServiceList(
                    services.map(s => s.code),
                    getSelectedService(),
                ),
            );
    }
};

export interface GeocoderConfig {
    getMapLayerInfos: Getter<ILayerInfo[]>;
    termConfig: {
        setView: Setter<IMapViewData>;
        setInteraction: Setter<Interaction>;
        termActions?: TermAction[];
    };
    layerActions?: LayerAction[];
}

export const geocoder = (cfg: GeocoderConfig) =>
    DIV(
        'geocoder-wrapper',
        renderServiceOrList(getAllServices()),
        getSelectedService().map(service =>
            DIV(
                `search-wrapper service-${service}`,
                renderService(
                    getServicePlaceholder(service),
                    service,
                    cfg.getMapLayerInfos,
                    cfg.termConfig,
                    cfg.layerActions ?? [],
                ),
            ),
        ),
    );

export const centerGeocoder = (
    setView: Setter<IMapViewData>,
    setInteraction: Setter<Interaction>,
) =>
    geocoder({
        getMapLayerInfos: () => [],
        termConfig: {
            setView,
            setInteraction,
        },
    });

export default geocoder;
